import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  InputAdornment,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  FormControlLabel,
  Switch,
  Autocomplete,
  Snackbar,
  Collapse,
  Divider,
  Table,
  TableBody,
  TableRow,
  TableCell
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Search, ContactMail, Edit, Close, Save, Business, PersonAdd, Sync, ExpandMore, ExpandLess } from '@mui/icons-material';
import api from '../services/api';

const Contacts = () => {
  const [contacts, setContacts] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchTimeout, setSearchTimeout] = useState(null);

  // Edit/Add dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('edit'); // 'edit' or 'add'
  const [editingContact, setEditingContact] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [sites, setSites] = useState([]);
  const [loadingSites, setLoadingSites] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // FreeScout preview state
  const [selectedSiteDetails, setSelectedSiteDetails] = useState(null);
  const [loadingSiteDetails, setLoadingSiteDetails] = useState(false);
  const [showFreescoutPreview, setShowFreescoutPreview] = useState(true);

  useEffect(() => {
    fetchContacts();
    fetchSites();
  }, []);

  const fetchContacts = async (search = '') => {
    try {
      setLoading(true);
      const params = search ? { search } : {};
      const response = await api.get('/sites/all/contacts', { params });
      setContacts(response.data.contacts);
      setTotalCount(response.data.total_count);
      setError('');
    } catch (error) {
      console.error('Error fetching contacts:', error);
      setError('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  };

  const fetchSites = async () => {
    try {
      setLoadingSites(true);
      const response = await api.get('/sites/all/sites-dropdown');
      setSites(response.data);
    } catch (error) {
      console.error('Error fetching sites:', error);
    } finally {
      setLoadingSites(false);
    }
  };

  const fetchSiteDetails = async (siteId) => {
    if (!siteId) {
      setSelectedSiteDetails(null);
      return;
    }
    try {
      setLoadingSiteDetails(true);
      const response = await api.get(`/sites/${siteId}`);
      setSelectedSiteDetails(response.data);
    } catch (error) {
      console.error('Error fetching site details:', error);
      setSelectedSiteDetails(null);
    } finally {
      setLoadingSiteDetails(false);
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    // Debounce search
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    setSearchTimeout(
      setTimeout(() => {
        fetchContacts(value);
      }, 500)
    );
  };

  const handleEditClick = (contact) => {
    setDialogMode('edit');
    setEditingContact(contact);
    setEditFormData({
      name: contact.name || '',
      email: contact.email || '',
      phone: contact.phone || '',
      title: contact.title || '',
      site_id: contact.site_id || null,
      account_name: contact.account_name || '',
      root_domain: contact.root_domain || '',
      do_not_call: contact.do_not_call || false,
      lead_source: contact.lead_source || '',
      notes: contact.notes || ''
    });
    // Fetch full site details if contact has a site
    if (contact.site_id) {
      fetchSiteDetails(contact.site_id);
    } else {
      setSelectedSiteDetails(null);
    }
    setDialogOpen(true);
  };

  const handleAddClick = () => {
    setDialogMode('add');
    setEditingContact(null);
    setEditFormData({
      name: '',
      email: '',
      phone: '',
      title: '',
      site_id: null,
      account_name: '',
      root_domain: '',
      do_not_call: false,
      lead_source: '',
      notes: ''
    });
    setSelectedSiteDetails(null);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingContact(null);
    setEditFormData({});
  };

  const handleInputChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSiteChange = (event, newValue) => {
    if (newValue) {
      setEditFormData(prev => ({
        ...prev,
        site_id: newValue.id,
        account_name: newValue.account_name || '',
        root_domain: newValue.rootdomain || ''
      }));
      // Fetch full site details for FreeScout preview
      fetchSiteDetails(newValue.id);
    } else {
      // Clear site connection and allow manual entry
      setEditFormData(prev => ({
        ...prev,
        site_id: null,
        account_name: '',
        root_domain: ''
      }));
      setSelectedSiteDetails(null);
    }
  };

  const handleSaveContact = async () => {
    // Validate required fields
    if (!editFormData.name || !editFormData.name.trim()) {
      setSnackbar({
        open: true,
        message: 'Name is required',
        severity: 'error'
      });
      return;
    }
    if (!editFormData.email || !editFormData.email.trim()) {
      setSnackbar({
        open: true,
        message: 'Email is required',
        severity: 'error'
      });
      return;
    }

    try {
      setSaving(true);

      if (dialogMode === 'add') {
        // Create new contact
        await api.post('/sites/all/contacts', editFormData);
        setSnackbar({
          open: true,
          message: 'Contact created successfully and synced to FreeScout',
          severity: 'success'
        });
      } else {
        // Update existing contact
        await api.put(`/sites/all/contacts/${editingContact.id}`, editFormData);
        setSnackbar({
          open: true,
          message: 'Contact updated successfully',
          severity: 'success'
        });
      }

      // Refresh contacts list
      await fetchContacts(searchTerm);
      handleDialogClose();
    } catch (error) {
      console.error('Error saving contact:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.detail || `Failed to ${dialogMode === 'add' ? 'create' : 'update'} contact`,
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const getSelectedSite = () => {
    if (!editFormData.site_id) return null;
    return sites.find(s => s.id === editFormData.site_id) || null;
  };

  // Build FreeScout preview data - show ALL fields even if empty
  const buildFreescoutPreview = () => {
    const name = editFormData.name || '';
    const nameParts = name.trim().split(/\s+/);
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';

    const preview = {
      customer: {
        firstName: firstName || (editFormData.email ? editFormData.email.split('@')[0] : null),
        lastName: lastName || null,
        email: editFormData.email || null,
        phone: editFormData.phone || null,
        jobTitle: editFormData.title || null,
        company: selectedSiteDetails?.account_name || editFormData.account_name || null,
        website: selectedSiteDetails?.website_url || (editFormData.root_domain ? `https://${editFormData.root_domain}` : null),
        notes: editFormData.notes || null
      },
      // Always show address section with site data
      address: {
        address: selectedSiteDetails?.billing_address || null,
        city: selectedSiteDetails?.billing_city || null,
        state: selectedSiteDetails?.billing_state || null,
        zip: selectedSiteDetails?.billing_postal_code || null
      },
      // Always show all custom fields
      customFields: [
        { name: 'Account Status', value: selectedSiteDetails?.account_status || null },
        { name: 'Shirt Size', value: selectedSiteDetails?.seo_shirt_size || null },
        { name: 'Service Notes', value: selectedSiteDetails?.services_notes || null },
        { name: 'CSM', value: selectedSiteDetails?.assigned_cs || null },
        { name: 'Strategist', value: selectedSiteDetails?.strategist || null }
      ]
    };

    return preview;
  };

  const columns = [
    { field: 'name', headerName: 'Name', flex: 1, minWidth: 150 },
    { field: 'email', headerName: 'Email', flex: 1, minWidth: 200 },
    { field: 'phone', headerName: 'Phone', width: 130 },
    { field: 'title', headerName: 'Title', width: 120 },
    { field: 'account_name', headerName: 'Account', width: 180 },
    { field: 'root_domain', headerName: 'Domain', width: 180 },
    {
      field: 'do_not_call',
      headerName: 'Do Not Call',
      width: 110,
      renderCell: (params) => (
        params.value ?
          <Chip label="Yes" color="error" size="small" /> :
          <Chip label="No" color="success" size="small" />
      )
    },
    { field: 'lead_source', headerName: 'Lead Source', width: 120 },
    {
      field: 'actions',
      headerName: 'Edit',
      width: 70,
      sortable: false,
      renderCell: (params) => (
        <IconButton
          size="small"
          onClick={() => handleEditClick(params.row)}
          color="primary"
        >
          <Edit fontSize="small" />
        </IconButton>
      )
    }
  ];

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{
              width: 44, height: 44, borderRadius: 2,
              bgcolor: 'primary.main', display: 'flex',
              alignItems: 'center', justifyContent: 'center', mr: 2,
              boxShadow: '0 4px 12px rgba(23, 75, 106, 0.25)'
            }}>
              <ContactMail sx={{ fontSize: 24, color: 'white' }} />
            </Box>
            <Box>
              <Typography variant="h4" component="h1" sx={{
                fontFamily: '"Cormorant Garamond", Georgia, serif',
                fontWeight: 500, letterSpacing: '-0.01em'
              }}>
                Contacts
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                All contacts synced from Monday.com — {totalCount.toLocaleString()} total
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            startIcon={<PersonAdd />}
            onClick={handleAddClick}
            sx={{
              bgcolor: 'primary.main',
              px: 3, py: 1.2,
              '&:hover': { bgcolor: 'primary.dark' }
            }}
          >
            Add Contact
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper sx={{
        p: 3, borderRadius: 3,
        boxShadow: '0px 12px 18px -6px rgba(0, 0, 0, 0.08)',
        border: '1px solid',
        borderColor: 'grey.200'
      }}>
        {/* Search Bar */}
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            placeholder="Search by name, email, account, or domain..."
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: 'grey.400' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: 'grey.100',
                borderRadius: 2,
                '& fieldset': { borderColor: 'transparent' },
                '&:hover fieldset': { borderColor: 'grey.300' },
                '&.Mui-focused fieldset': { borderColor: 'primary.main' }
              }
            }}
          />
        </Box>

        {/* Data Grid */}
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={36} sx={{ color: 'primary.main' }} />
          </Box>
        ) : (
          <DataGrid
            rows={contacts}
            columns={columns}
            autoHeight
            pageSize={25}
            rowsPerPageOptions={[25, 50, 100]}
            disableSelectionOnClick
            sx={{
              border: 'none',
              minHeight: 500,
              '& .MuiDataGrid-columnHeaders': {
                bgcolor: 'grey.100',
                borderRadius: '8px 8px 0 0',
                borderBottom: '2px solid',
                borderColor: 'grey.200',
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                fontWeight: 600,
                fontSize: '0.8rem',
                letterSpacing: '0.03em',
                textTransform: 'uppercase',
                color: 'text.secondary'
              },
              '& .MuiDataGrid-cell': {
                py: 1.5,
                borderColor: 'grey.100',
                fontSize: '0.875rem'
              },
              '& .MuiDataGrid-row:hover': {
                bgcolor: 'rgba(23, 75, 106, 0.04)'
              },
              '& .MuiDataGrid-footerContainer': {
                borderTop: '2px solid',
                borderColor: 'grey.200'
              }
            }}
          />
        )}
      </Paper>

      {/* Add/Edit Contact Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{
                width: 36, height: 36, borderRadius: 2,
                bgcolor: dialogMode === 'add' ? 'primary.main' : 'info.main',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                {dialogMode === 'add'
                  ? <PersonAdd sx={{ fontSize: 20, color: 'white' }} />
                  : <Edit sx={{ fontSize: 20, color: 'white' }} />
                }
              </Box>
              <Typography sx={{
                fontFamily: '"Cormorant Garamond", Georgia, serif',
                fontWeight: 500, fontSize: '1.4rem'
              }}>
                {dialogMode === 'add' ? 'Add New Contact' : 'Edit Contact'}
              </Typography>
            </Box>
            <IconButton
              onClick={handleDialogClose}
              size="small"
              sx={{
                color: 'grey.400',
                '&:hover': { bgcolor: 'grey.100', color: 'grey.600' }
              }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3} sx={{ mt: 0 }}>
            {/* Basic Info */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Name"
                value={editFormData.name || ''}
                onChange={handleInputChange('name')}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={editFormData.email || ''}
                onChange={handleInputChange('email')}
                required
                disabled={dialogMode === 'edit'}
                helperText={dialogMode === 'edit' ? 'Email cannot be changed' : 'Must be unique'}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                value={editFormData.phone || ''}
                onChange={handleInputChange('phone')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Title"
                value={editFormData.title || ''}
                onChange={handleInputChange('title')}
              />
            </Grid>

            {/* Connected Account */}
            <Grid item xs={12}>
              <Typography variant="overline" sx={{
                color: 'primary.main', fontWeight: 600,
                letterSpacing: '0.1em', fontSize: '0.7rem', mb: 1, display: 'block'
              }}>
                Connected Account
              </Typography>
              <Autocomplete
                options={sites}
                getOptionLabel={(option) =>
                  option.account_name
                    ? `${option.account_name} (${option.rootdomain})`
                    : option.rootdomain
                }
                value={getSelectedSite()}
                onChange={handleSiteChange}
                loading={loadingSites}
                clearOnEscape
                filterOptions={(options, { inputValue }) => {
                  const searchTerm = inputValue.toLowerCase();
                  if (!searchTerm) return options.slice(0, 50); // Show first 50 when no search
                  return options.filter(option =>
                    (option.account_name && option.account_name.toLowerCase().includes(searchTerm)) ||
                    (option.rootdomain && option.rootdomain.toLowerCase().includes(searchTerm))
                  ).slice(0, 50);
                }}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <Business sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {option.account_name || 'No Account Name'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.rootdomain}
                        </Typography>
                      </Box>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Account"
                    placeholder="Type to search by account name or domain..."
                    helperText={editFormData.site_id
                      ? "Connected to an account. Clear to disconnect."
                      : "No account connected. Select one or enter details manually below."}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <Search sx={{ color: 'text.secondary' }} />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                noOptionsText="No accounts found - try a different search term"
                ListboxProps={{ style: { maxHeight: 300 } }}
              />
            </Grid>

            {/* Account Name and Root Domain - editable when no site connected */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Account Name"
                value={editFormData.account_name || ''}
                onChange={handleInputChange('account_name')}
                disabled={!!editFormData.site_id}
                helperText={editFormData.site_id
                  ? "Auto-filled from connected account"
                  : "Enter account name manually"}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Root Domain"
                value={editFormData.root_domain || ''}
                onChange={handleInputChange('root_domain')}
                disabled={!!editFormData.site_id}
                helperText={editFormData.site_id
                  ? "Auto-filled from connected account"
                  : "Enter root domain manually"}
              />
            </Grid>

            {/* Additional Fields */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Lead Source"
                value={editFormData.lead_source || ''}
                onChange={handleInputChange('lead_source')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={editFormData.do_not_call || false}
                    onChange={handleInputChange('do_not_call')}
                    color="error"
                  />
                }
                label="Do Not Call"
              />
            </Grid>

            {/* Notes */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                value={editFormData.notes || ''}
                onChange={handleInputChange('notes')}
                multiline
                rows={3}
              />
            </Grid>

            {/* FreeScout Preview Section */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1, borderColor: 'grey.200' }} />
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  mb: 1,
                  py: 1,
                  px: 1.5,
                  borderRadius: 2,
                  transition: 'background-color 0.2s ease',
                  '&:hover': { bgcolor: 'grey.100' }
                }}
                onClick={() => setShowFreescoutPreview(!showFreescoutPreview)}
              >
                <Sync sx={{ color: 'primary.main', mr: 1.5, fontSize: 20 }} />
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                    FreeScout Sync Preview
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Data that will be sent to FreeScout when saving
                  </Typography>
                </Box>
                {loadingSiteDetails ? (
                  <CircularProgress size={18} sx={{ color: 'primary.main' }} />
                ) : showFreescoutPreview ? (
                  <ExpandLess sx={{ color: 'grey.400' }} />
                ) : (
                  <ExpandMore sx={{ color: 'grey.400' }} />
                )}
              </Box>

              <Collapse in={showFreescoutPreview}>
                {(() => {
                  const preview = buildFreescoutPreview();
                  const renderValue = (value) => {
                    if (value === null || value === undefined || value === '') {
                      return <Typography component="span" color="text.disabled" sx={{ fontStyle: 'italic' }}>null</Typography>;
                    }
                    return value;
                  };
                  return (
                    <Paper variant="outlined" sx={{
                      p: 2.5, bgcolor: 'grey.100',
                      borderColor: 'grey.200', borderRadius: 3
                    }}>
                      {/* Customer Info */}
                      <Typography variant="overline" sx={{
                        color: 'primary.main', fontWeight: 600,
                        letterSpacing: '0.1em', fontSize: '0.65rem', mb: 1, display: 'block'
                      }}>
                        Customer Info
                      </Typography>
                      <Table size="small" sx={{ mb: 2 }}>
                        <TableBody>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 'medium', width: 120, border: 0, py: 0.5 }}>First Name</TableCell>
                            <TableCell sx={{ border: 0, py: 0.5 }}>{renderValue(preview.customer.firstName)}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 'medium', border: 0, py: 0.5 }}>Last Name</TableCell>
                            <TableCell sx={{ border: 0, py: 0.5 }}>{renderValue(preview.customer.lastName)}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 'medium', border: 0, py: 0.5 }}>Email</TableCell>
                            <TableCell sx={{ border: 0, py: 0.5 }}>{renderValue(preview.customer.email)}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 'medium', border: 0, py: 0.5 }}>Phone</TableCell>
                            <TableCell sx={{ border: 0, py: 0.5 }}>{renderValue(preview.customer.phone)}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 'medium', border: 0, py: 0.5 }}>Job Title</TableCell>
                            <TableCell sx={{ border: 0, py: 0.5 }}>{renderValue(preview.customer.jobTitle)}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 'medium', border: 0, py: 0.5 }}>Company</TableCell>
                            <TableCell sx={{ border: 0, py: 0.5 }}>{renderValue(preview.customer.company)}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 'medium', border: 0, py: 0.5 }}>Website</TableCell>
                            <TableCell sx={{ border: 0, py: 0.5 }}>{renderValue(preview.customer.website)}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 'medium', border: 0, py: 0.5 }}>Notes</TableCell>
                            <TableCell sx={{ border: 0, py: 0.5 }}>{renderValue(preview.customer.notes)}</TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>

                      {/* Address (from Site) */}
                      <Typography variant="overline" sx={{
                        color: 'primary.main', fontWeight: 600,
                        letterSpacing: '0.1em', fontSize: '0.65rem', mb: 1, display: 'block'
                      }}>
                        Address (from Site)
                      </Typography>
                      {!selectedSiteDetails && (
                        <Alert severity="info" sx={{ mb: 2, py: 0 }}>
                          Connect to a site to populate address fields
                        </Alert>
                      )}
                      <Table size="small" sx={{ mb: 2 }}>
                        <TableBody>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 'medium', width: 120, border: 0, py: 0.5 }}>Street</TableCell>
                            <TableCell sx={{ border: 0, py: 0.5 }}>{renderValue(preview.address.address)}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 'medium', border: 0, py: 0.5 }}>City</TableCell>
                            <TableCell sx={{ border: 0, py: 0.5 }}>{renderValue(preview.address.city)}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 'medium', border: 0, py: 0.5 }}>State</TableCell>
                            <TableCell sx={{ border: 0, py: 0.5 }}>{renderValue(preview.address.state)}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 'medium', border: 0, py: 0.5 }}>ZIP</TableCell>
                            <TableCell sx={{ border: 0, py: 0.5 }}>{renderValue(preview.address.zip)}</TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>

                      {/* Custom Fields (from Site) */}
                      <Typography variant="overline" sx={{
                        color: 'primary.main', fontWeight: 600,
                        letterSpacing: '0.1em', fontSize: '0.65rem', mb: 1, display: 'block'
                      }}>
                        Custom Fields (from Site)
                      </Typography>
                      {!selectedSiteDetails && (
                        <Alert severity="info" sx={{ mb: 2, py: 0 }}>
                          Connect to a site to populate custom fields
                        </Alert>
                      )}
                      <Table size="small">
                        <TableBody>
                          {preview.customFields.map((field, index) => (
                            <TableRow key={index}>
                              <TableCell sx={{ fontWeight: 'medium', width: 120, border: 0, py: 0.5 }}>{field.name}</TableCell>
                              <TableCell sx={{ border: 0, py: 0.5 }}>{renderValue(field.value)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Paper>
                  );
                })()}
              </Collapse>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleDialogClose}
            disabled={saving}
            sx={{
              color: 'text.secondary', fontWeight: 500, px: 2.5,
              '&:hover': { bgcolor: 'grey.100' }
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveContact}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={18} sx={{ color: 'white' }} /> : <Save />}
            sx={{
              px: 3, borderRadius: 2,
              bgcolor: 'primary.main',
              boxShadow: 'none',
              '&:hover': {
                bgcolor: 'primary.dark',
                boxShadow: '0 4px 12px rgba(23, 75, 106, 0.25)'
              }
            }}
          >
            {saving ? 'Saving...' : (dialogMode === 'add' ? 'Create Contact' : 'Save Changes')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Contacts;
